import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';

// `score_summary` is a comma-joined per-set string ("21-15, 18-25, 15-10"),
// built server-side (tournamentScheduleController.js's enrichGameWithCheckins)
// from game_common's score data — see scheduleApi.js's buildLiveData doc
// comment. Parsed here rather than trusting participant_a/b_score alone,
// since those two columns hold SETS WON once a match has multiple sets, not
// a displayable score on their own.
export const parseSets = (liveData) => {
  const summary = liveData?.score_summary;
  if (summary) {
    const sets = summary.split(',').map((pair) => {
      const [a, b] = pair.trim().split('-').map(Number);
      return { a, b };
    }).filter((set) => Number.isFinite(set.a) && Number.isFinite(set.b));
    if (sets.length > 0) return sets;
  }
  const { participant_a_score: a, participant_b_score: b } = liveData || {};
  if (a != null && b != null) return [{ a, b }];
  return [];
};

/**
 * Replaces the Score column's plain "vs" for a completed match. A single
 * set (e.g. Basketball's one final score) just shows that score directly,
 * matching ScheduleTab.jsx's own behavior (:1759-1760, 1892-1893). Multiple
 * sets show the sets-won tally instead, expandable to each set's own score —
 * showing every point total up front reads as noise once there's more than
 * one number pair, so it's tucked behind a click.
 */
const MatchScoreCell = ({ row }) => {
  const { theme } = useTheme();
  const [expanded, setExpanded] = useState(false);

  if (row.status !== 'completed') {
    return <span className={`text-center text-xs font-semibold ${theme.textMuted}`}>vs</span>;
  }

  const sets = parseSets(row.liveData);
  if (sets.length === 0) {
    return <span className={`text-center text-xs font-semibold ${theme.textMuted}`}>vs</span>;
  }

  if (sets.length === 1) {
    const { a, b } = sets[0];
    return <span className={`whitespace-nowrap text-sm font-bold ${theme.text}`}>{a} – {b}</span>;
  }

  const setsWonA = sets.filter((set) => set.a > set.b).length;
  const setsWonB = sets.filter((set) => set.b > set.a).length;

  return (
    <div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((v) => !v);
        }}
        className={`flex items-center justify-center gap-1 whitespace-nowrap text-sm font-bold ${theme.text}`}
      >
        {setsWonA} – {setsWonB}
        {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </button>
      {expanded && (
        <div className={`mt-1 space-y-0.5 whitespace-nowrap text-xs ${theme.textMuted}`} onClick={(e) => e.stopPropagation()}>
          {sets.map((set, i) => (
            <div key={i}>Set {i + 1}: {set.a} – {set.b}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MatchScoreCell;
